import React from "react";
import DataSourceContainer from "./components/DataSourceContainer";
import { IntegrationStore } from "./stores/IntegrationsStore";
import InputContainer from "./components/InputContainer";
import AcxMainTextField from "components/UI/AcxMainTextField";
import moment from "moment";
import AcxCalendarPopup from "components/UI/Calendar/AcxCalendarPopup";
import { Box, Divider, Switch, Typography } from "@mui/material";
import theme from "Theme/AppTheme";
import { observer } from "mobx-react";
import { hiddenValue } from "./helpers/constants";
import { toIso } from "utils/DateTimeUtils";
import ErrorHandler from "./components/ErrorHandler";

type AmazonConnectProps = {
    store: IntegrationStore;
};

const AmazonConnect: React.FC<AmazonConnectProps> = observer(({ store }) => {
    const {
        selectedIntegration,
        updateSelectedIntegration,
        integrationKeys,
        hasIntegrationKey,
        setIntegrationKeys,
        isCheckKeysLoading,
        hasError,
    } = store;

    if (!selectedIntegration) {
        return null;
    }

    const {
        configuration: {
            customConfiguration: {
                AudioDirectoryName,
                BucketName,
                BucketRegion,
                InstanceId,
                InstanceRegion,
                ChatDirectoryName,
                InstanceAlias,
            },
        },
    } = selectedIntegration;

    const setCustomConfiguration = (key: string, value: string) => {
        updateSelectedIntegration({
            ...selectedIntegration,
            configuration: {
                ...selectedIntegration.configuration,
                customConfiguration: {
                    ...selectedIntegration.configuration.customConfiguration,
                    [key]: value,
                },
            },
        });
    };

    return (
        <DataSourceContainer heading="Connection Settings">
            <ErrorHandler
                hasError={hasError.amazonDbFields || hasError.amazonKeys}
                errorMessage={
                    hasError.amazonDbFields
                        ? "Please enter all the required fields to continue."
                        : "Please enter all the required keys to continue."
                }
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <InputContainer label="URL">
                    <AcxMainTextField
                        fullWidth
                        id="integration-info-url"
                        value={selectedIntegration.url}
                        placeholderText="Enter url"
                        onChange={(e) =>
                            updateSelectedIntegration({
                                ...selectedIntegration,
                                url: e.target.value,
                            })
                        }
                        error={
                            hasError.amazonDbFields && !selectedIntegration.url
                        }
                        hideErrorIcon
                    />
                </InputContainer>
                <InputContainer
                    label="No Calls Prior To Date"
                    sx={{
                        button: {
                            background: "transparent",
                            borderTop: `1px solid ${theme.palette.lightgrayBorder.main}`,
                            borderBottom: `1px solid ${theme.palette.lightgrayBorder.main}`,
                            borderRight: `1px solid ${theme.palette.lightgrayBorder.main}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            svg: {
                                color: theme.palette.gray[400],
                                fontSize: "24px",
                            },
                        },
                    }}
                >
                    <AcxCalendarPopup
                        id="integration-info-no-calls-prior-to-date"
                        inputStyle={{
                            borderRadius: theme.shape.borderRadius,
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            borderRight: "none",
                            borderTop: `1px solid ${theme.palette.lightgrayBorder.main}`,
                            borderBottom: `1px solid ${theme.palette.lightgrayBorder.main}`,
                            borderLeft: `1px solid ${theme.palette.lightgrayBorder.main}`,
                            paddingRight: theme.spacing(1),
                            paddingLeft: theme.spacing(1),
                        }}
                        currentDate={selectedIntegration.lastUpdate ? moment(
                            selectedIntegration.lastUpdate,
                        ) : undefined}
                        onSelect={(date) =>
                            updateSelectedIntegration({
                                ...selectedIntegration,
                                lastUpdate: toIso(date.utc()),
                            })
                        }
                    />
                </InputContainer>
                <InputContainer label="API Key">
                    <AcxMainTextField
                        fullWidth
                        skeleton={isCheckKeysLoading}
                        id="integration-amazon-access-key"
                        value={integrationKeys.amazonAccessKey}
                        placeholderText={
                            hasIntegrationKey["amazon-access-key"]
                                ? hiddenValue
                                : "Enter API key"
                        }
                        onChange={(e) => {
                            setIntegrationKeys({
                                ...integrationKeys,
                                amazonAccessKey: e.target.value,
                            });
                        }}
                        error={
                            hasError.amazonKeys &&
                            !integrationKeys.amazonAccessKey
                        }
                        hideErrorIcon
                    />
                </InputContainer>
                <InputContainer label="Secret Key">
                    <AcxMainTextField
                        fullWidth
                        skeleton={isCheckKeysLoading}
                        id="integration-amazon-secret-key"
                        value={integrationKeys.amazonSecretKey}
                        placeholderText={
                            hasIntegrationKey["amazon-secret-key"]
                                ? hiddenValue
                                : "Enter secret key"
                        }
                        onChange={(e) =>
                            setIntegrationKeys({
                                ...integrationKeys,
                                amazonSecretKey: e.target.value,
                            })
                        }
                        error={
                            hasError.amazonKeys &&
                            !integrationKeys.amazonSecretKey
                        }
                        hideErrorIcon
                    />
                </InputContainer>
                <InputContainer label="Active">
                    <Switch
                        id="integration-info-active"
                        sx={{ width: 40, marginLeft: 0 }}
                        onChange={() =>
                            updateSelectedIntegration({
                                ...selectedIntegration,
                                isActive: !selectedIntegration.isActive,
                            })
                        }
                        checked={selectedIntegration.isActive}
                    />
                </InputContainer>
            </Box>
            <Typography
                component="h2"
                sx={{
                    fontWeight: 600,
                    fontSize: "16px",
                    color: theme.palette.gray[700],
                    marginTop: 4,
                }}
            >
                Amazon Connect Database Fields
            </Typography>
            <Divider
                sx={{
                    margin: theme.spacing(1, 0, 2),
                }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <InputContainer label="Bucket Name">
                    <AcxMainTextField
                        fullWidth
                        id="amazon-connect-bucket-name"
                        value={BucketName}
                        placeholderText="Enter bucket name"
                        onChange={(e) =>
                            setCustomConfiguration("BucketName", e.target.value)
                        }
                        error={hasError.amazonDbFields && !BucketName}
                        hideErrorIcon
                    />
                </InputContainer>
                <InputContainer label="Bucket Region">
                    <AcxMainTextField
                        fullWidth
                        id="amazon-connect-bucket-region"
                        value={BucketRegion}
                        placeholderText="Enter bucket region"
                        onChange={(e) =>
                            setCustomConfiguration(
                                "BucketRegion",
                                e.target.value,
                            )
                        }
                        error={hasError.amazonDbFields && !BucketRegion}
                        hideErrorIcon
                    />
                </InputContainer>
                <InputContainer label="Instance Region">
                    <AcxMainTextField
                        fullWidth
                        id="amazon-connect-instance-region"
                        value={InstanceRegion}
                        placeholderText="Enter instance region"
                        onChange={(e) =>
                            setCustomConfiguration(
                                "InstanceRegion",
                                e.target.value,
                            )
                        }
                        error={hasError.amazonDbFields && !InstanceRegion}
                        hideErrorIcon
                    />
                </InputContainer>
                <InputContainer label="Instance Alias">
                    <AcxMainTextField
                        fullWidth
                        id="amazon-connect-instance-alias"
                        value={InstanceAlias}
                        placeholderText="Enter instance alias"
                        onChange={(e) =>
                            setCustomConfiguration(
                                "InstanceAlias",
                                e.target.value,
                            )
                        }
                        error={hasError.amazonDbFields && !InstanceAlias}
                        hideErrorIcon
                    />
                </InputContainer>
                <InputContainer label="Instance ID">
                    <AcxMainTextField
                        fullWidth
                        id="amazon-connect-instance-id"
                        value={InstanceId}
                        placeholderText="Enter instance id"
                        onChange={(e) =>
                            setCustomConfiguration("InstanceId", e.target.value)
                        }
                        error={hasError.amazonDbFields && !InstanceId}
                        hideErrorIcon
                    />
                </InputContainer>

                <InputContainer label="Audio Directory Name">
                    <AcxMainTextField
                        fullWidth
                        id="amazon-connect-audio-directory-name"
                        value={AudioDirectoryName}
                        placeholderText="Enter audio directory name"
                        onChange={(e) =>
                            setCustomConfiguration(
                                "AudioDirectoryName",
                                e.target.value,
                            )
                        }
                        error={hasError.amazonDbFields && !AudioDirectoryName}
                        hideErrorIcon
                    />
                </InputContainer>
                <InputContainer label="Chat Directory Name">
                    <AcxMainTextField
                        fullWidth
                        id="amazon-connect-chat-directory-name"
                        value={ChatDirectoryName}
                        placeholderText="Enter chat directory name"
                        onChange={(e) =>
                            setCustomConfiguration(
                                "ChatDirectoryName",
                                e.target.value,
                            )
                        }
                        error={hasError.amazonDbFields && !ChatDirectoryName}
                        hideErrorIcon
                    />
                </InputContainer>
            </Box>
        </DataSourceContainer>
    );
});

export default AmazonConnect;
